import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Optional,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AfterBlockDragService } from '../../blocks/rich-text-block/after-block-drag.service';
import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { ButtonBlockComponent } from '../../blocks/button-block/button-block.component';
import { ContactCardComponent } from '../../blocks/contact-card/contact-card.component';
import { DividerBlockComponent } from '../../blocks/divider-block/divider-block.component';
import { HeadingBlockComponent } from '../../blocks/heading-block/heading-block.component';
import { HtmlBlockComponent } from '../../blocks/html-block/html-block.component';
import { ImageAndTextBlockComponent } from '../../blocks/image-and-text-block/image-and-text-block.component';
import { ImageBlockComponent } from '../../blocks/image-block/image-block.component';
import { SimpleTableBlockComponent } from '../../blocks/simple-table-block/simple-table-block.component';
import { SpacerBlockComponent } from '../../blocks/spacer-block/spacer-block.component';
import { EmailBlockPaddingComponent } from '../email-block-padding/email-block-padding.component';

import {
  blankContentListItem,
  blockType,
  ContentListItem,
  defaultBlockChoices,
  optionalBlockTypes,
  type OptionalBlockIds,
} from '../../types/contentblocks';

import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { ActivatableApp } from '@vendasta/marketplace-packages';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { blankButtonBlock, ButtonBlock } from '../../blocks/button-block/button-block.component';
import { blankContactCardBlock } from '../../blocks/contact-card/contact-card.component';
import { blankDividerBlock } from '../../blocks/divider-block/divider-block.component';
import { blankHeadingBlock } from '../../blocks/heading-block/heading-block.component';
import { blankHtmlBlock } from '../../blocks/html-block/html-block.component';
import { blankImageAndTextBlock } from '../../blocks/image-and-text-block/image-and-text-block.component';
import { blankImageBlock, ImageBlock } from '../../blocks/image-block/image-block.component';
import {
  blankReviewRequestLinksBlock,
  ReviewRequestLinksBlockComponent,
} from '../../blocks/review-request-links-block/review-request-links-block.component';
import {
  blankRichTextBlock,
  RichTextBlock,
  RichTextBlockComponent,
} from '../../blocks/rich-text-block/rich-text-block.component';
import { blankSimpleTableBlock } from '../../blocks/simple-table-block/simple-table-block.component';
import { blankSpacerBlock } from '../../blocks/spacer-block/spacer-block.component';
import {
  AppListingServiceInterface,
  EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN,
} from '../../services/app-listing-service.interface';
import { FeatureFlagEnabledService } from '../../services/feature-flag.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { jsonCopy } from '../../shared';
import { SuggestContentComponent } from '../suggest-content-dialog/suggest-content-dialog.component';
import { blankNetPromoterScoreLinksBlock, NpsBlockComponent } from '../../blocks/nps-block/nps-block.component';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';

export const EMAIL_BUILDER_ENABLED_RECIPES = new InjectionToken<string[]>(
  'string[]: list of recipe IDs to enable within the builder',
);

interface RecipeListItem {
  id: string;
  source?: string;
  name: string;
  block: ContentListItem;
  contentPlaceholder?: string;
}

const productButtonRecipe: RecipeListItem = {
  id: 'product-button',
  source: 'Marketplace',
  name: 'Product page button',
  block: {
    type: 'button',
    content: {
      buttonText: `{{ productName['PRODID'] }}`,
      link: `{{ productPreviewUrl['PRODID'] }}`,
      buttonStyle: 'solid',
      buttonColor: '#47b75d',
      showBackupLink: false,
    },
  },
  contentPlaceholder: 'PRODID',
};

const meetingSchedulerButtonRecipe: RecipeListItem = {
  id: 'meeting-scheduler-button',
  source: 'Meeting Scheduler',
  name: 'Book a meeting button',
  block: {
    type: 'button',
    content: {
      buttonText: `Book a meeting`,
      link: `{{ meetingSchedulerUrl }}`,
      buttonStyle: 'solid',
      buttonColor: '#47b75d',
      showBackupLink: false,
    },
  },
};

const suggestTextContentRecipe: RecipeListItem = {
  id: 'suggest-text-content',
  name: 'Text',
  block: {
    type: 'rich_text',
    content: {},
  },
};

const suggestImageContentRecipe: RecipeListItem = {
  id: 'suggest-image-content',
  name: 'Image',
  block: {
    type: 'image',
    content: {},
  },
};

const buttonRecipes: RecipeListItem[] = [productButtonRecipe, meetingSchedulerButtonRecipe];

@Component({
  selector: 'email-blocks-list',
  templateUrl: './email-blocks-list.component.html',
  styleUrls: ['./email-blocks-list.component.scss'],
  providers: [
    {
      provide: 'CONTENT_GENERATION_ENABLED',
      useFactory: (flagEnabledService: FeatureFlagEnabledService) =>
        flagEnabledService.isEnabled('email_builder_content_generation'),
      deps: [FeatureFlagEnabledService],
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    TranslateModule,
    GalaxyButtonGroupModule,
    GalaxyFormFieldModule,
    ButtonBlockComponent,
    ContactCardComponent,
    DividerBlockComponent,
    HeadingBlockComponent,
    HtmlBlockComponent,
    ImageAndTextBlockComponent,
    ImageBlockComponent,
    SimpleTableBlockComponent,
    SpacerBlockComponent,
    EmailBlockPaddingComponent,
    MatAutocomplete,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatOption,
    RichTextBlockComponent,
    ReviewRequestLinksBlockComponent,
    NpsBlockComponent,
  ],
})
export class EmailBlocksListComponent implements OnInit {
  @HostBinding('class') class = 'email-blocks-list';
  @Input() blocks: Array<ContentListItem>;

  @Input() set optionalBlockTypes(value: OptionalBlockIds[]) {
    if (value && value.length >= 1) {
      const blocksToAdd = [];

      value.forEach((item) => {
        const found = optionalBlockTypes.find((c) => c.id === item);
        if (found) blocksToAdd.push(found);
      });

      if (blocksToAdd.length >= 1) {
        this.blockNamesAndIcons = this.blockNamesAndIcons.concat(blocksToAdd);
      }
    }
  }

  @ViewChild('productList', { static: true }) productList: TemplateRef<any>;

  public productButtonRecipe = productButtonRecipe;
  public buttonRecipes: RecipeListItem[] = [];
  public suggestContentRecipes: RecipeListItem[] = [suggestTextContentRecipe, suggestImageContentRecipe];

  public products$: Observable<ActivatableApp[]>;

  blockNamesAndIcons = defaultBlockChoices;

  productSearchControl = new UntypedFormControl();
  filteredProductList$: Observable<ActivatableApp[]>;

  constructor(
    private emailPreview: EmailPreviewRefreshService,
    private afterBlockDrag: AfterBlockDragService,
    public dialog: MatDialog,
    @Inject(EMAIL_BUILDER_ENABLED_RECIPES) @Optional() public enabledRecipes: string[],
    @Inject(EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN) @Optional() private appListingService: AppListingServiceInterface,
    private posthogService: ProductAnalyticsService,
    private _: GalaxyAiIconService,
    private viewContainerRef: ViewContainerRef,
    private imageUploadService: ImageUploadService,
    @Inject('CONTENT_GENERATION_ENABLED') @Optional() public contentGenerationEnabled$: Observable<boolean>,
  ) {}

  ngOnInit(): void {
    //TODO: Once recipes are stored in the backend, this mechanism will be replaced with a GetMulti
    if (this.enabledRecipes) {
      this.enabledRecipes.forEach((recipeID) =>
        this.buttonRecipes.push(buttonRecipes.find((value) => value.id === recipeID)),
      );
    }
    if (this.appListingService) {
      this.products$ = this.appListingService.listAllActivatableApps();
    } else {
      this.products$ = of([]);
      this.buttonRecipes = this.buttonRecipes.filter((r) => r.name !== 'Product button');
    }
    this.filteredProductList$ = this.productSearchControl.valueChanges.pipe(
      startWith(''),
      switchMap((value: string) => (value !== '' ? this._filter(value) : this.products$)),
    );
  }

  handleBlockValidityEvent(event: boolean, index: number): void {
    this.blocks[index].valid = event;
  }

  draggingEvents(): void {
    // This is used to update the rich text editor after drag happens
    // otherwise content disappears inside the tinyMce editor
    window.setTimeout(() => {
      this.afterBlockDrag.dragHappened();
    }, 0);
  }

  checkName(input: string): Observable<string> {
    // check if the block name is in the main block list
    let found = this.blockNamesAndIcons.find((item) => item.id === input);
    // if not, we might have an optional block that snuck through
    if (!found) found = optionalBlockTypes.find((item) => item.id === input);
    if (found) {
      return of(found.name);
    } else {
      return of(input);
    }
  }

  refreshPreview(): void {
    this.emailPreview.refresh();
  }

  toggleAllBlocks(collapsed: boolean, event): void {
    if (event.altKey) {
      this.blocks.forEach((block) => {
        block.collapsed = collapsed;
      });
    }
  }

  dropBlock(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.blocks, event.previousIndex, event.currentIndex);
    this.draggingEvents();
    this.emailPreview.refresh();
  }

  moveBlock(index, delta, inputArray = this.blocks): void {
    const newIndex = index + delta;

    //Already at the top or bottom.
    if (newIndex < 0 || newIndex === inputArray.length) return;

    moveItemInArray(this.blocks, index, newIndex);
    this.draggingEvents();
    this.emailPreview.refresh();
  }

  moveBlockToTop(index, inputArray = this.blocks): void {
    moveItemInArray(inputArray, index, 0);
    this.draggingEvents();
    this.emailPreview.refresh();
  }

  moveBlockToBottom(index, inputArray = this.blocks): void {
    moveItemInArray(this.blocks, index, inputArray.length);
    this.draggingEvents();
    this.emailPreview.refresh();
  }

  deleteBlock(index: number, inputArray = this.blocks): void {
    if (index > -1) {
      inputArray.splice(index, 1);
    }
    this.emailPreview.refresh();
  }

  duplicateBlock(index: number, inputArray = this.blocks): void {
    if (index > -1) {
      const blockToDuplicate = jsonCopy(inputArray[index]) as ContentListItem;
      inputArray.splice(index, 0, blockToDuplicate);
    }
    this.emailPreview.refresh();
  }

  async addNewBlock(
    newBlockType: blockType,
    renderType?: blockType,
    index?: number,
    inputArray = this.blocks,
  ): Promise<void> {
    const newBlockObject = jsonCopy(blankContentListItem) as ContentListItem;

    // give the newly created object a "type" set to the current block type
    newBlockObject.type = newBlockType;
    newBlockObject.renderType = renderType;

    // adds it's blank state content
    switch (newBlockType) {
      case 'image': {
        newBlockObject.content = jsonCopy(blankImageBlock);
        break;
      }
      case 'rich_text': {
        newBlockObject.content = jsonCopy(blankRichTextBlock);
        break;
      }
      case 'heading': {
        newBlockObject.content = jsonCopy(blankHeadingBlock);
        break;
      }
      case 'button': {
        newBlockObject.content = jsonCopy(blankButtonBlock);
        break;
      }
      case 'image_and_text': {
        newBlockObject.content = jsonCopy(blankImageAndTextBlock);
        break;
      }
      case 'simple_table': {
        newBlockObject.content = jsonCopy(blankSimpleTableBlock);
        break;
      }
      case 'contact_card': {
        newBlockObject.content = jsonCopy(blankContactCardBlock);
        break;
      }
      case 'divider': {
        newBlockObject.content = jsonCopy(blankDividerBlock);
        break;
      }
      case 'spacer': {
        newBlockObject.content = jsonCopy(blankSpacerBlock);
        break;
      }
      case 'html': {
        newBlockObject.content = jsonCopy(blankHtmlBlock);
        break;
      }
      case 'suggest_content': {
        await this.openSuggestContentDialog();
        break;
      }
      case 'review_request_links': {
        newBlockObject.content = jsonCopy(blankReviewRequestLinksBlock);
        break;
      }
      case 'net_promoter_score': {
        newBlockObject.content = jsonCopy(blankNetPromoterScoreLinksBlock);
        break;
      }
      default: {
        console.error('No new block type with name ' + newBlockType);
        return;
      }
    }

    if (newBlockObject.type !== 'suggest_content') {
      if (index > -1) {
        inputArray.splice(index, 0, newBlockObject);
      } else {
        inputArray.push(newBlockObject);
      }
    }

    this.emailPreview.refresh();
  }

  addRecipe(recipe: RecipeListItem, contentToReplacePlaceholder = '', index?: number): void {
    const newBlockObject = jsonCopy(blankContentListItem) as ContentListItem;
    const newContent = jsonCopy(recipe.block.content);

    // give the newly created object a "type" set to the current block type
    newBlockObject.type = recipe.block.type;
    newBlockObject.renderType = recipe.block.renderType;

    newBlockObject.content = newContent;

    if (recipe.block.type == 'button') {
      if (contentToReplacePlaceholder !== '' && recipe.contentPlaceholder) {
        const content = newContent as ButtonBlock;
        content.buttonText = content.buttonText.replace(recipe.contentPlaceholder, contentToReplacePlaceholder);
        content.link = content.link.replace(recipe.contentPlaceholder, contentToReplacePlaceholder);
        newBlockObject.content = content;
      }
    } else if (recipe.block.type == 'image') {
      if (contentToReplacePlaceholder !== '') {
        const content = newContent as ImageBlock;
        content.url = contentToReplacePlaceholder;
        content.imageStyle = 'fullWidth';
        newBlockObject.content = content;
      }
    } else if (recipe.block.type == 'rich_text') {
      if (contentToReplacePlaceholder !== '') {
        const content = newContent as RichTextBlock;
        content.rawHtml = contentToReplacePlaceholder;
        newBlockObject.content = content;
      }
    }

    if (index > -1) {
      this.blocks.splice(index, 0, newBlockObject);
    } else {
      this.blocks.push(newBlockObject);
    }

    this.emailPreview.refresh();
  }

  openProductDialog(data: { index: number }): void {
    this.dialog.open(this.productList, { width: '350px', data: data });
  }

  async openSuggestContentDialog(): Promise<void> {
    this.posthogService.trackEvent('user-clicked-suggest-content-block', 'suggest-content', 'click', null);
    const dialogRef = this.dialog.open(SuggestContentComponent, {
      viewContainerRef: this.viewContainerRef,
      width: '600px',
      height: 'auto',
    });
    const text = await firstValueFrom(dialogRef.afterClosed());
    if (text) {
      this.addRecipe(suggestTextContentRecipe, text);
    }
  }

  async openSuggestImageDialog(): Promise<void> {
    await this.imageUploadService.generateAndUploadImages().then((selectedImage) => {
      if (selectedImage) {
        selectedImage
          .pipe(
            take(1),
            map((img) => {
              this.addRecipe(suggestImageContentRecipe, img);
            }),
          )
          .subscribe();
      }
    });
  }

  private _filter(searchValue: string): Observable<ActivatableApp[]> {
    if (!searchValue) {
      return this.products$;
    }

    const filterValue = searchValue.toLowerCase();
    return this.products$.pipe(
      take(1),
      map((products) => products.filter((option) => option.name.toLowerCase().includes(filterValue))),
    );
  }
}
